// filepath: apps/web/src/features/lists/tree/types.ts
//
// Local wire-shape aliases for the Tree module.
//
// Mirrors the backend DTOs from internal/entity/view_config.go so the
// tree module stays self-contained and does not ripple type changes
// into the broader types barrel.
//
// Wire shape: view_config = { default_view_type, tree? }. The
// dispatched route (EntityListPage.tsx) only mounts <TreeView/>
// when default_view_type === "tree" so the optional block never
// reaches the renderer.

import type { EntityRecordWire } from "../types";

export interface EntityViewConfigWire {
  default_view_type: "table" | "kanban" | "gantt" | "tree";
  kanban?: unknown;
  tree?: EntityTreeConfigWire;
}

export interface EntityTreeConfigWire {
  parent_field: string;
  depth_cap?: number;
}

export interface TreeRecord {
  id: string;
  parentValue: string | null;
  rowVersion: number;
  fields: Readonly<Record<string, unknown>>;
}

export function normaliseTreeRecord(
  r: EntityRecordWire,
  parentField: string,
): TreeRecord | null {
  const rawVersion = (r as unknown as { row_version?: unknown }).row_version;
  const altRawVersion = r.RowVersion;
  const rowVersion =
    typeof rawVersion === "number"
      ? rawVersion
      : typeof rawVersion === "string" && /^[0-9]+$/.test(rawVersion)
        ? Number.parseInt(rawVersion, 10)
        : typeof altRawVersion === "number"
          ? altRawVersion
          : typeof altRawVersion === "string" && /^[0-9]+$/.test(altRawVersion)
            ? Number.parseInt(altRawVersion, 10)
            : null;
  // INV-7 — a record without a usable row_version OCC token
  // cannot be safely re-sent for an update; the renderer refuses
  // to display it. RowVersion 0 is treated as "no token" because
  // 0 is not a valid OCC counter.
  if (rowVersion === null || rowVersion <= 0) return null;

  const fieldsMap = (r.Fields ?? {}) as Record<string, unknown>;
  const rawParent = fieldsMap[parentField];
  let parentValue: string | null = null;
  if (typeof rawParent === "string") {
    parentValue = rawParent.length > 0 ? rawParent : null;
  } else if (typeof rawParent === "number") {
    parentValue = String(rawParent);
  }
  return {
    id: r.ID,
    parentValue,
    rowVersion,
    fields: fieldsMap,
  };
}
